import { useEffect, useState } from 'react';
import logo from 'src/images/logo-md.png';
import {
  Badge,
  Button,
  Card,
  Col,
  Icon,
  Input,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useScreenSize
} from 'straightline-ui';
import {
  calculatePaymentFee,
  formatMoney,
  formatPaymentFee
} from 'straightline-utils/accounting';
import { PAYMENT_METHOD } from 'straightline-utils/constants';
import { formatDate } from 'straightline-utils/time';

const statusColor = {
  PAID: 'soft-success',
  OPEN: 'soft-primary',
  VOID: 'soft-secondary',
  UNCOLLECTIBLE: 'soft-danger'
};

export function ShipmentInvoice({ shipmentInvoice, id, className = '' }) {
  return (
    <Card className={`p-0 ${className}`}>
      <div className="p-4 pb-0" id={id}>
        <Row className="align-items-center">
          <Col sm="6" className="mb-3">
            <img
              src={logo}
              alt="..."
              className="img-fluid"
              style={{ maxHeight: 26 }}
            />
          </Col>
          <Col sm="6" className="mb-3 text-sm-right">
            <div className="h2 mb-0" style={{ lineHeight: 1 }}>
              #{shipmentInvoice.id}
            </div>
          </Col>
        </Row>
        <hr className="mt-0 mb-4" />
        <Row>
          <Col sm="6" className="mb-4">
            <h6 className="text-uppercase text-muted">Invoiced From</h6>
            <p className="mb-0">
              <strong className="text-body">
                Straightsoft, Inc. dba Straightline
              </strong>
              <br />
              3750 Hacks Cross Rd <br />
              Memphis, TN, 38119 US <br />
              901-937-9896 <br />
              accounting@bookstraightline.com
            </p>
          </Col>
          <Col sm="6" className="text-sm-right mb-4">
            <h6 className="text-uppercase text-muted">Invoiced To</h6>
            <p className="mb-0">
              <strong className="text-body">
                {shipmentInvoice.shipper.name}
              </strong>
              {shipmentInvoice.shipper.address && (
                <>
                  <br />
                  {shipmentInvoice.shipper.address.address_1} <br />
                  {shipmentInvoice.shipper.address.address_2 && (
                    <>
                      {shipmentInvoice.shipper.address.address_2} <br />
                    </>
                  )}
                  {shipmentInvoice.shipper.address.city},{' '}
                  {shipmentInvoice.shipper.address.state},{' '}
                  {shipmentInvoice.shipper.address.postal_code}{' '}
                  {shipmentInvoice.shipper.address.country}
                </>
              )}
              <br />
              {shipmentInvoice.shipper.billing_emails[0]}
            </p>
          </Col>
        </Row>
        <hr className="mt-0 mb-4" />
        <Row className="align-items-center">
          <Col xs="auto" className="mb-4">
            <h6 className="text-uppercase text-muted">Shipment ID</h6>
            <p className="mb-0">#{shipmentInvoice.shipment_id}</p>
          </Col>
          <Col xs="auto" className="mb-4">
            <h6 className="text-uppercase text-muted">Ref ID</h6>
            <p className="mb-0">
              {shipmentInvoice.shipment.reference_number || 'NA'}
            </p>
          </Col>
          <Col xs="auto" className="mb-4">
            <h6 className="text-uppercase text-muted">Shipment Date</h6>
            <time>
              {formatDate(shipmentInvoice.shipment.pickup_date, 'short')}
            </time>
          </Col>
          <Col xs="auto" className="ml-auto text-right mb-4">
            <h6 className="text-uppercase text-muted">Due Date</h6>
            <p className="mb-0">
              {formatDate(shipmentInvoice.due_date, 'short')}
            </p>
          </Col>
          <Col xs="auto" className="text-right mb-4">
            <h6 className="text-uppercase text-muted">Status</h6>
            <Badge color={statusColor[shipmentInvoice.status]}>
              {shipmentInvoice.status}
            </Badge>
          </Col>
        </Row>
        <hr className="my-0" />
        <div className="table-responsive">
          <Table className="table mb-0">
            <TableHead>
              <TableRow>
                <TableHeader className="px-0 bg-transparent border-top-0">
                  <span className="h6">Description</span>
                </TableHeader>
                <TableHeader className="px-0 bg-transparent border-top-0 text-right">
                  <span className="h6">Cost</span>
                </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {(shipmentInvoice.items || []).map((item, index) => (
                <TableRow key={index}>
                  <TableData className="px-0">
                    <div>{item.name}</div>
                    <div className="text-secondary">{item.description}</div>
                  </TableData>
                  <TableData className="px-0 text-right">
                    {formatMoney(item.amount)}
                  </TableData>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="d-flex justify-content-between border-top pt-4">
          <div>
            <h3 className="mb-0">Amount Due</h3>
          </div>
          <div>
            <h3 className="mb-0">{formatMoney(shipmentInvoice.due_amount)}</h3>
          </div>
        </div>
        {shipmentInvoice.notes && (
          <>
            <hr className="my-4" />
            <h6 className="text-uppercase text-muted">Notes</h6>
            <p className="mb-0 pre-line">{shipmentInvoice.notes}</p>
          </>
        )}
      </div>
    </Card>
  );
}

export function PayInvoiceModal({
  onPay,
  onCancel,
  shipmentInvoice,
  paymentMethods,
  open
}) {
  // const { sm } = useScreenSize();

  return (
    <Modal
      isOpen={open}
      // style={{ maxWidth: sm ? 1000 : '100%' }}
      size="lg"
      toggle={onCancel}
      contentClassName="h-100"
      modalClassName="modal-fullscreen-md"
      scrollable
      centered
    >
      {() => (
        <PayInvoiceModalContent
          onPay={onPay}
          onCancel={onCancel}
          shipmentInvoice={shipmentInvoice}
          paymentMethods={paymentMethods}
        />
      )}
    </Modal>
  );
}

function PayInvoiceModalContent({
  onPay,
  onCancel,
  shipmentInvoice,
  paymentMethods
}) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (paymentMethods?.length > 0 && !selectedPaymentMethod) {
      const defaultMethod = paymentMethods.find((method) => {
        return method.default_method;
      });
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod);
      }
    }
  }, [paymentMethods, selectedPaymentMethod]);

  if (!shipmentInvoice) {
    return null;
  }

  const outstandingAmount =
    shipmentInvoice.due_amount - shipmentInvoice.paid_amount;

  let feeAmount = 0;
  if (selectedPaymentMethod) {
    feeAmount = calculatePaymentFee(outstandingAmount, selectedPaymentMethod);
  }

  const totalAmount = outstandingAmount + feeAmount;

  const handlePayInvoice = async () => {
    if (!selectedPaymentMethod) {
      return;
    }

    try {
      setIsLoading(true);
      await onPay({
        shipment_invoice_id: shipmentInvoice.id,
        payment_method_id: selectedPaymentMethod.id
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.errors(error);
    }
  };

  if (outstandingAmount <= 0) {
    return (
      <>
        <ModalBody className="p-0">
          <ShipmentInvoice
            shipmentInvoice={shipmentInvoice}
            className="border-0 shadow-none mb-0"
          />
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <Button color="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <ModalBody className="p-0">
        <ShipmentInvoice
          shipmentInvoice={shipmentInvoice}
          className="border-0 shadow-none mb-0"
        />
        <div className="p-4 bg-lighter border-top border-bottom">
          <h3>Select Payment Method</h3>
          <div>
            Choose a payment method to pay your invoice. Additional fees may be
            added to the invoice total based on the selected payment method.
            Contact Straightline at{' '}
            <a href="mailto:accounting@bookstraightline.com">
              accounting@bookstraightline.com
            </a>{' '}
            with any questions.
          </div>
        </div>
        <div className="p-4">
          {shipmentInvoice.paid_amount > 0 && (
            <div className="alert alert-primary">
              <div>
                You have already paid {formatMoney(shipmentInvoice.paid_amount)}{' '}
                on this invoice! This charge amount and fees will be based off
                the outstanding balance of {formatMoney(outstandingAmount)}
              </div>
            </div>
          )}
          <PaymentMethodSelector
            paymentMethods={paymentMethods}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            disabled={isLoading}
            amount={outstandingAmount}
          />
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <Button color="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handlePayInvoice}
          loading={isLoading}
          disabled={isLoading}
          style={{ minWidth: 200, fontSize: 18 }}
        >
          <span className="mr-2">Pay</span>
          {selectedPaymentMethod ? (
            <span className="animated fadeIn fast" key={totalAmount}>
              {formatMoney(totalAmount)}
            </span>
          ) : (
            'Invoice'
          )}
        </Button>
      </ModalFooter>
    </>
  );
}

export function PaymentMethodSelector({
  paymentMethods,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  disabled,
  amount
}) {
  return (
    <ListGroup>
      {paymentMethods?.map((paymentMethod) => {
        return (
          <ListGroupItem
            key={paymentMethod.id}
            action={true}
            tag="button"
            type="button"
            active={selectedPaymentMethod?.id === paymentMethod.id}
            className={
              selectedPaymentMethod?.id === paymentMethod.id ? 'bg-lighter' : ''
            }
            style={{
              backgroundColor:
                paymentMethod.id === selectedPaymentMethod?.id ? '#e0edff' : ''
            }}
            onClick={() => {
              setSelectedPaymentMethod(paymentMethod);
            }}
            disabled={disabled}
          >
            <Row className="align-items-start">
              <Col xs="auto" className="pr-0">
                <Icon
                  className="text-secondary"
                  type={paymentMethodIcons[paymentMethod.type] || 'edit-3'}
                  style={{ fontSize: 35, lineHeight: 1 }}
                />
              </Col>
              <Col>
                <Row className="d-flex justify-content-between align-items-start">
                  <Col>
                    <div className="text-body">
                      {paymentMethod.description} ending in{' '}
                      {paymentMethod.reference_number}
                    </div>
                    <div className="text-secondary">
                      {formatPaymentFee(paymentMethod)}
                    </div>
                    <div className="text-secondary">
                      Expires:{' '}
                      {paymentMethod.exp_month
                        ? `${paymentMethod.exp_month}/${paymentMethod.exp_year}`
                        : 'Never'}
                    </div>
                  </Col>
                  <Col sm="auto">
                    <ProcessingFee
                      selectedPaymentMethod={selectedPaymentMethod}
                      paymentMethod={paymentMethod}
                      action={true}
                      amount={calculatePaymentFee(amount, paymentMethod)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );
}

const paymentMethodIcons = {
  [PAYMENT_METHOD.CARD]: 'credit-card',
  [PAYMENT_METHOD.BANK_ACCOUNT]: 'home'
};

function ProcessingFee({
  amount,
  selectedPaymentMethod,
  paymentMethod,
  action
}) {
  const { md } = useScreenSize();

  return (
    <div className="d-flex align-items-center py-1 justify-content-end-sm">
      {!md && action && (
        <div className="mr-3">
          <Input
            type="radio"
            className="mr-n2"
            // bsSize="lg"
            checked={selectedPaymentMethod?.id === paymentMethod.id}
          />
        </div>
      )}
      <div
        className={
          selectedPaymentMethod?.id === paymentMethod.id
            ? 'text-primary h3 mb-0'
            : 'h3 mb-0'
        }
      >
        <div className="">{amount ? formatMoney(amount) : 'Free'}</div>
      </div>
      {md && action && (
        <div className="ml-3">
          <Input
            type="radio"
            className="mr-n2"
            // bsSize="lg"
            checked={selectedPaymentMethod?.id === paymentMethod.id}
          />
        </div>
      )}
    </div>
  );
}
